import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { CheckIcon } from 'icons';
import styles from './index.module.sass';
import classnames from 'classnames/bind';
import InfiniteScroll from 'react-infinite-scroll-component';

const cx = classnames.bind(styles);

/**
 * @typedef DataItem
 *
 * @property {string} key - key
 * @property {string} value - value
 */

/**
 * @callback onSelect
 *
 * @param {string} value - selected value
 * @returns {undefined}
 */

/**
 * ### DataList
 *
 * @param {{string | null}} value - selected value
 * @param {string} className - custom class name
 * @param {Array<DataItem>} data - { label: string; value: string; }
 * @param {boolean} show - isShow
 * @param {boolean} withIcon - with icon or not (default: true)
 * @param {onSelect} onSelect - callback function to get the selected value
 * @param {string} placeholder - placeholder
 * @param {string} className - custom class name
 * @param {function} render - render function
 *
 * @returns {React.ReactNode}
 */

const DataList = ({ className = '', data, value: currentValue = '', show, onSelect, withIcon = true, render }) => {
  const [currentLength, setCurrentLength] = useState(20);
  const [hasMore, setHasMore] = useState(data.length > 20);
  const [newData, setNewData] = useState([]);

  useEffect(() => {
    setNewData(() => data.slice(0, currentLength));
    setHasMore(data.length > currentLength);
  }, [data, currentLength]);

  const listClass = cx(className, {
    list: true,
    show,
  });

  const fetchData = () => {
    let isAll = currentLength + 20 > data.length;
    let newLength = isAll ? data.length : currentLength + 20;
    setHasMore(!isAll);
    setCurrentLength(newLength);
    setNewData(() => {
      return data.slice(0, newLength);
    });
  };

  const DataMap = () => {
    if (newData.length === 0) return <div className={styles.empty}>無資料</div>;

    return newData.map(({ label, value }) => {
      const activate = currentValue === value && value !== 'default';

      const SelectedIcon = () =>
        activate ? <CheckIcon width={24} height={24} style={{ marginLeft: '10px' }} /> : null;

      const itemClass = cx({
        item: true,
        active: activate,
      });

      return (
        <div key={value} className={itemClass} onClick={() => onSelect(value)}>
          {render ? render(label) : <div>{label}</div>}
          {withIcon && <SelectedIcon />}
        </div>
      );
    });
  };

  return (
    <div className={listClass} id="list">
      <InfiniteScroll
        scrollableTarget="list"
        dataLength={currentLength} //This is important field to render the next data
        next={fetchData}
        hasMore={hasMore}
      >
        <DataMap />
      </InfiniteScroll>
    </div>
  );
};

DataList.defaultProps = {
  withIcon: true,
};

DataList.propTypes = {
  data: propTypes.array,
  value: propTypes.string,
  show: propTypes.bool,
  onSelect: propTypes.func,
  withIcon: propTypes.bool,
};

export default DataList;
